type LayoutShiftSource = {
  node: Node | null
}

type LayoutShift = PerformanceEntry & {
  hadRecentInput: boolean
  sources?: LayoutShiftSource[]
  value: number
}

// Below this CLS is "good", so there is nothing to attribute and no extra beacons
const minReportedValue = 0.1
// Session window limits from the CLS definition
const maxShiftGap = 1000
const maxWindowDuration = 5000
const maxSelectorDepth = 4
const maxSelectorLength = 100

const dataKeys = ['clsElement', 'clsValue', 'clsTime']

let windowValue = 0
let windowStart = 0
let lastShiftTime = 0
let largestShift: LayoutShift = null
let reportedValue = 0

const describeElement = (element: Element): string => {
  const testId = element.getAttribute('data-testid')
  if (testId) {
    return testId
  }
  if (element.id) {
    return `#${element.id}`
  }
  // Generated styled-components classes say nothing, keep readable ones such as fresnel-*
  const readableClass = Array.from(element.classList)
    .filter((name) => /^(?!sc-)[a-z]+-[a-zA-Z]/.test(name))
    .pop()
  const tagName = element.tagName.toLowerCase()
  return readableClass ? `${tagName}.${readableClass}` : tagName
}

const getSelector = (node: Node): string => {
  const path: string[] = []
  let element = node instanceof Element ? node : node.parentElement
  while (element && path.length < maxSelectorDepth) {
    path.unshift(describeElement(element))
    if (element.id || element.hasAttribute('data-testid')) {
      break
    }
    element = element.parentElement
  }
  return path.join(' > ').slice(-maxSelectorLength)
}

const report = () => {
  const node = largestShift?.sources?.find((source) => source.node)?.node
  if (!node) {
    return
  }
  reportedValue = windowValue
  window.LUX.addData('clsElement', getSelector(node))
  window.LUX.addData('clsValue', Number(windowValue.toFixed(3)))
  window.LUX.addData('clsTime', Math.round(largestShift.startTime))
}

const onLayoutShift = (shift: LayoutShift) => {
  if (shift.hadRecentInput) {
    return
  }
  const isNewWindow =
    shift.startTime - lastShiftTime >= maxShiftGap ||
    shift.startTime - windowStart >= maxWindowDuration
  if (isNewWindow) {
    windowValue = 0
    windowStart = shift.startTime
    largestShift = null
  }
  windowValue += shift.value
  lastShiftTime = shift.startTime
  if (!largestShift || shift.value > largestShift.value) {
    largestShift = shift
  }
}

/**
 * Sends the element behind the worst layout shift window to SpeedCurve as custom data.
 * Values are sent as soon as they change, LUX adds them to its beacon or sends a supplementary one.
 */
export const observeClsAttribution = (): (() => void) => {
  if (!PerformanceObserver.supportedEntryTypes?.includes('layout-shift')) {
    return () => undefined
  }
  const observer = new PerformanceObserver((list) => {
    list.getEntries().forEach(onLayoutShift)
    if (windowValue >= minReportedValue && windowValue > reportedValue) {
      report()
    }
  })
  observer.observe({ type: 'layout-shift', buffered: true })
  return () => observer.disconnect()
}

/**
 * LUX keeps custom data between pages, so a new page has to start without the old values.
 */
export const resetClsAttribution = (): void => {
  windowValue = 0
  windowStart = 0
  lastShiftTime = 0
  largestShift = null
  reportedValue = 0
  dataKeys.forEach((key) => window.LUX.addData(key, null))
}
